import React, { useEffect, useMemo, useState } from "react";
import { useMessage } from "../../../context/MessageContext";
import { postApi, putApi } from "../../../apis/api";
import { GENERAL_ERROR_MESSAGE } from "../../../utils/message";
import { XCrossIcon } from "../../../assets";
import { ADD_SERVICES, UPDATE_SERVICES } from "../../../apis/end-points";
import isEqual from "lodash/isEqual";

interface AddSerivesModalType {
  handleClose: () => void;
  setLoading: (value: boolean) => void;
  refreshList?: () => void;
  data?: any;
}

interface ServicesFields {
  id?: string;
  serviceName: string;
  onwardServiceName: string;
}

interface ErrorFields {
  serviceName: string;
}

const OmniServiceModal: React.FC<AddSerivesModalType> = ({
  handleClose,
  setLoading,
  refreshList,
  data,
}) => {
  const { setMessage } = useMessage();

  const initialFormState: ServicesFields = {
    id: "",
    serviceName: "",
    onwardServiceName: "",
  };

  const [formData, setFormData] = useState<ServicesFields>(initialFormState);
  const [originalData, setOriginalData] = useState<ServicesFields>(initialFormState);

  const [errors, setErrors] = useState<ErrorFields>({
    serviceName: "",
  });

  useEffect(() => {
    if (data?.id) {
      const populated: ServicesFields = {
        id: data?.id || "",
        serviceName: data?.serviceName || "",
        onwardServiceName: data?.onwardServiceName === "--" ? "": data?.onwardServiceName || "",
      };
      setFormData(populated);
      setOriginalData(populated); // Save original for comparison
    } else {
      setFormData(initialFormState);
      setOriginalData(initialFormState);
    }
  }, [data]);

  const validate = (): boolean => {
    const newErrors: ErrorFields = {
      serviceName: "",
    };
    let isValid = true;

    if (!formData.serviceName.trim()) {
      newErrors.serviceName = "Service Name is required.";
      isValid = false;
    } else if (formData.serviceName.trim().length < 2) {
      newErrors.serviceName = "Service Name must be at least 2 characters.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value.trimStart(),
    }));
    setErrors((prev) => ({
      ...prev,
      [id]: "",
    }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const apiPayload = {
        name: formData.serviceName,
        onwardServiceName: formData.onwardServiceName,
      };

      let apiResponse;
      if (formData?.id) {
        apiResponse = await putApi(`${UPDATE_SERVICES}${formData?.id}`, apiPayload);
      } else {
        apiResponse = await postApi(ADD_SERVICES, apiPayload);
      }

      if (apiResponse.success) {
        setMessage(
          formData?.id ? "Onward service name updated successfully!" : "Service mapping created successfully!",
          "success"
        );
        refreshList && refreshList();
        handleClose();
      } else {
        setMessage(apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
      }
    } catch (error) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const isButtonDisabled = useMemo(() => {
    const hasEmptyFields = !formData.serviceName.trim();

    const hasNoChanges = isEqual(formData, originalData);

    return hasEmptyFields || hasNoChanges;
  }, [formData, originalData]);

  return (
    <div className="modal modal--hazmatDetail modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--lg">
          <div className="modal__header">
            <h4 className="modal__header--title">
              {formData.id ? "Edit Service" : "Add Service"}
            </h4>
            <button className="btn btn__transparent btnClose" onClick={handleClose}>
              <XCrossIcon />
            </button>
          </div>

          <div className="modal__body">
            <div className="formRow formRow__2">
              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="serviceName" className="form__label">
                    Service Name <span className="mandatory-mark">*</span>
                  </label>
                  <input
                    type="text"
                    id="serviceName"
                    className={`form__input form__input--sm ${
                      formData?.id ? "form__input--disabled" : ""
                    }`}
                    placeholder="Service Name"
                    value={formData.serviceName}
                    disabled={!!formData?.id}
                    onChange={handleFieldChange}
                  />
                  {errors.serviceName && (
                    <span className="form__error">{errors.serviceName}</span>
                  )}
                </div>
              </div>

              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="onwardServiceName" className="form__label">
                    Onward Service Name
                  </label>
                  <input
                    type="text"
                    id="onwardServiceName"
                    className="form__input form__input--sm"
                    placeholder="Onward Service Name"
                    value={formData.onwardServiceName}
                    onChange={handleFieldChange}
                    onKeyDown={(e) => {
                      if (e.key === " " && formData.onwardServiceName.endsWith(",")) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal__footer">
            <div className="btn__group">
              <button className="btn btn__white" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="btn btn__primary"
                disabled={isButtonDisabled}
                onClick={handleSubmit}
              >
                {formData?.id ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};

export default OmniServiceModal;